import React, { useState, useEffect, useCallback } from 'react';
import { Button, Form, InputGroup } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHashtag, faCopy, faCheck } from '@fortawesome/free-solid-svg-icons';
import CryptoJS from 'crypto-js';
import RouteSeo from '../components/RouteSeo';
import ToolContent from '../components/ToolContent';
import ToolLayout from '../components/ToolLayout';
import { HASH_ABOUT } from '../content/toolGuides';
import { HASH_ROUTE } from '../content/tools';

interface HashResult {
  name: string;
  value: string;
}

const HASH_ALGORITHMS = [
  { name: 'MD5', func: (text: string) => CryptoJS.MD5(text).toString() },
  { name: 'SHA-1', func: (text: string) => CryptoJS.SHA1(text).toString() },
  { name: 'SHA-256', func: (text: string) => CryptoJS.SHA256(text).toString() },
  { name: 'SHA-512', func: (text: string) => CryptoJS.SHA512(text).toString() },
  { name: 'SHA-3', func: (text: string) => CryptoJS.SHA3(text).toString() },
  { name: 'RIPEMD-160', func: (text: string) => CryptoJS.RIPEMD160(text).toString() },
];

const HashGenerator: React.FC = () => {
  const [input, setInput] = useState('');
  const [hashes, setHashes] = useState<HashResult[]>([]);
  const [copiedHash, setCopiedHash] = useState<string | null>(null);
  const [autoUpdate, setAutoUpdate] = useState(true);

  const generateHashes = useCallback((text: string) => {
    if (!text) {
      setHashes([]);
      return;
    }

    const results: HashResult[] = HASH_ALGORITHMS.map(algo => ({
      name: algo.name,
      value: algo.func(text),
    }));

    setHashes(results);
  }, []);

  useEffect(() => {
    if (autoUpdate) {
      generateHashes(input);
    }
  }, [input, autoUpdate, generateHashes]);

  const handleGenerateClick = () => {
    generateHashes(input);
  };

  const copyToClipboard = async (text: string, hashName: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedHash(hashName);
      setTimeout(() => setCopiedHash(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const loadExample = () => {
    setInput('Hello, World!');
  };

  return (
    <>
      <RouteSeo route={HASH_ROUTE} />
      <ToolLayout
        title="Hash Generator"
        description="Generate cryptographic hashes for your text"
        icon={faHashtag}
        controls={
          <>
            <div className="tool-controls__group">
              <Form.Check
                type="checkbox"
                id="auto-update"
                label="Auto-update hashes"
                checked={autoUpdate}
                onChange={(e) => setAutoUpdate(e.target.checked)}
                inline
              />
              <Button variant="link" size="sm" onClick={loadExample} className="px-0">
                Load Example
              </Button>
            </div>
            {!autoUpdate && (
              <Button variant="primary" onClick={handleGenerateClick} disabled={!input}>
                Generate Hashes
              </Button>
            )}
          </>
        }
        inputLabel="Input Text"
        inputValue={input}
        onInputChange={setInput}
        inputPlaceholder="Enter text to hash..."
        inputRows={5}
        inputHint="All hashing is performed locally in your browser - your data never leaves your device."
        outputContent={
          hashes.length > 0 ? (
            <>
              <h3 className="h5 mb-3">Generated Hashes</h3>
              {hashes.map((hash) => (
                <div key={hash.name} className="mb-3">
                  <Form.Label className="fw-bold">{hash.name}</Form.Label>
                  <InputGroup>
                    <Form.Control
                      type="text"
                      value={hash.value}
                      readOnly
                      className="font-monospace bg-light hash-value"
                    />
                    <Button
                      variant="outline-secondary"
                      onClick={() => copyToClipboard(hash.value, hash.name)}
                      aria-label={`Copy ${hash.name} hash`}
                    >
                      <FontAwesomeIcon icon={copiedHash === hash.name ? faCheck : faCopy} />
                    </Button>
                  </InputGroup>
                </div>
              ))}
            </>
          ) : (
            <p className="text-muted mb-0">Hashes will appear here as you type...</p>
          )
        }
        aboutTitle="About Hash Functions"
        about={HASH_ABOUT}
      >
        <ToolContent route={HASH_ROUTE} />
      </ToolLayout>
    </>
  );
};

export default HashGenerator;
